/**
 * CAD format detection — small pure module shared by the viewer shell,
 * the layer list and the block accordion so all three agree on which
 * file extensions land where.
 *
 * Keep this module zero-dep (no React, no Three.js, no Tailwind) so it
 * can be imported by both the lazy `CadViewerImpl` chunk (which pulls
 * Three.js) AND the cheap shell `CadViewerSwitch` (which must stay out
 * of the main bundle's heavy graph).
 */

export type CadFormat = "dxf" | "step" | "iges" | "stl";

/**
 * Resolve a CAD format from a filename, MIME type, or full URL.
 *
 * Resolution order:
 *   1. Explicit MIME types we know about
 *   2. File extension (DXF → 2D drawing, STEP/STP → 3D model,
 *      IGES/IGS → legacy 3D, STL → mesh)
 *   3. ``null`` when nothing matches — caller renders a "formato non
 *      riconosciuto" fallback.
 */
export function detectCadFormat(
  source: string | null | undefined,
): CadFormat | null {
  if (!source) return null;
  const lower = source.toLowerCase();

  // MIME-type direct hit — checked first because filenames often lie
  // (e.g. ``drawing.bin`` with ``Content-Type: image/vnd.dxf``).
  if (lower.includes("image/vnd.dxf") || lower.includes("application/dxf")) {
    return "dxf";
  }
  if (lower.includes("application/step") || lower.includes("model/step")) {
    return "step";
  }
  if (lower.includes("model/iges") || lower.includes("application/iges")) {
    return "iges";
  }
  if (lower.includes("model/stl") || lower.includes("application/sla")) {
    return "stl";
  }

  // Extension match — strip query string + hash first.
  const path = lower.split("?")[0]?.split("#")[0] ?? lower;
  if (path.endsWith(".dxf")) return "dxf";
  // DWG → DXF (Aug 25, 2026 — RAINHA incident). Platform invariant: the
  // ingestion pipeline converts every DWG via ODA File Converter and
  // persists the CONVERTED DXF as the row's source bytes
  // (``_persist_cad_source_bytes`` receives ``dxf_bytes``), so every URL
  // a viewer receives for a ``.dwg`` row serves parseable DXF text — the
  // filename is the only thing still saying "dwg". No surface feeds raw
  // DWG bytes to the viewer (chat chips render name-only; both viewer
  // mounts read pipeline-persisted objects). If that invariant ever
  // breaks, the DXF parser's ``onParseError`` shows the typed error
  // state — strictly more informative than "formato non riconosciuto".
  if (path.endsWith(".dwg") || lower.includes("image/vnd.dwg")) return "dxf";
  if (path.endsWith(".step") || path.endsWith(".stp")) return "step";
  if (path.endsWith(".iges") || path.endsWith(".igs")) return "iges";
  if (path.endsWith(".stl")) return "stl";

  return null;
}

/**
 * Map a resolved format to the parser/viewer pipeline that handles it.
 * Useful for the shell to decide whether to mount the 2D vs 3D viewer
 * without re-deriving the boolean each time.
 */
export const FORMAT_META: Record<CadFormat, { readonly is3D: boolean; readonly label: string }> = {
  dxf: { is3D: false, label: "DXF" },
  step: { is3D: true, label: "STEP" },
  iges: { is3D: true, label: "IGES" },
  stl: { is3D: true, label: "STL" },
};
